import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import apiHelper from '../../utils/apiHelpers';
const config = require('../../config/config');

// The bulk imports that can be triggered by hand, matching the targets accepted by
// GET /crawler/import/:target. Data-driven because there are six of them and they differ
// only by target and label.
const IMPORT_TARGETS = [
  ['all', 'Importar todo (Scryfall, reglas y combos)'],
  ['cards', 'Importar cartas de Scryfall'],
  ['rulings', 'Importar rulings'],
  ['tags', 'Importar etiquetas y roles'],
  ['rules', 'Importar Reglas Comprehensivas'],
  ['combos', 'Importar combos'],
];

/**
 * A clickable menu row. Same markup as the hand-written entries above and below it; the
 * import buttons use this rather than a seventh copy of the block.
 */
const MenuAction = ({ collapsed, iconClass, label, onClick }) => (
  <li>
    <div
      onClick={onClick}
      className={`cursor-pointer flex ${
        collapsed ? 'flex-row justify-center' : ''
      } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
      <i className={`fas ${iconClass} ${collapsed ? '' : 'mr-2'}`}></i>{' '}
      {collapsed ? '' : label}
    </div>
  </li>
);

const SideMenu = () => {
  const user = apiHelper.getUsername();
  const [collapsed, setCollapsed] = useState(true);
  // The imports run for minutes and answer immediately with whether they started, so
  // unlike the crawler buttons there is something worth showing — in particular the 409
  // when one is already running, since a second replace-all over the same tables would
  // collide.
  const [importMessage, setImportMessage] = useState('');

  const onClickExpand = () => {
    setCollapsed(!collapsed);
  };

  const onClickImport = target => {
    setImportMessage('Iniciando...');
    apiHelper
      .runImport(target)
      .then(response => setImportMessage(response.data.message))
      .catch(error =>
        setImportMessage(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
            'No se pudo iniciar la importación.',
        ),
      );
  };

  return (
    <>
      {/* Overlay when the sidebar is expanded */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10"
          onClick={onClickExpand}></div>
      )}

      <nav
        className={`overflow-y-auto transition-all ease-in-out duration-300 ${
          collapsed ? 'w-20' : 'w-full sm:w-64'
        } bg-mediumColor text-white h-full fixed top-0 left-0 z-20`}>
        <div
          className="flex flex-col items-center justify-center p-4 border-b border-gray-700 cursor-pointer"
          onClick={onClickExpand}>
          <img
            src={process.env.PUBLIC_URL + '/img/isologo.png'}
            width="170px"
            alt="ManaValue"
          />
          {!collapsed && (
            <span className="text-white text-2xl font-bold">{user}</span>
          )}
        </div>
        <ul className="space-y-2 p-4">
          <li>
            <Link
              to={`${process.env.PUBLIC_URL}/findcard`}
              onClick={() => setCollapsed(true)}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i className={`fas fa-search ${collapsed ? '' : 'mr-2'}`}></i>{' '}
              {collapsed ? '' : 'Buscar carta'}
            </Link>
          </li>
          <li>
            <Link
              to={`${process.env.PUBLIC_URL}/findbulk`}
              onClick={() => setCollapsed(true)}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i className={`fas fa-list-ol ${collapsed ? '' : 'mr-2'}`}></i>{' '}
              {collapsed ? '' : 'Buscar lista de cartas'}
            </Link>
          </li>
          <li>
            <Link
              to={`${process.env.PUBLIC_URL}/deckbuilder`}
              onClick={() => setCollapsed(true)}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i
                className={`fas fa-layer-group ${
                  collapsed ? '' : 'mr-2'
                }`}></i>{' '}
              {collapsed ? '' : 'Deck builder'}
            </Link>
          </li>
          <li>
            <Link
              to={`${process.env.PUBLIC_URL}/wishlist`}
              onClick={() => setCollapsed(true)}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i className={`fas fa-heart ${collapsed ? '' : 'mr-2'}`}></i>{' '}
              {collapsed ? '' : 'Wishlist'}
            </Link>
          </li>
          <li>
            <Link
              to={`${process.env.PUBLIC_URL}/collection`}
              onClick={() => setCollapsed(true)}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i className={`fas fa-boxes ${collapsed ? '' : 'mr-2'}`}></i>{' '}
              {collapsed ? '' : 'Colección'}
            </Link>
          </li>
          <li>
            <Link
              to={`${process.env.PUBLIC_URL}/shoppingcart`}
              onClick={() => setCollapsed(true)}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i
                className={`fas fa-shopping-cart ${
                  collapsed ? '' : 'mr-2'
                }`}></i>{' '}
              {collapsed ? '' : 'Carrito de compras'}
            </Link>
          </li>
          <li>
            <Link
              to={`${process.env.PUBLIC_URL}/profile`}
              onClick={() => setCollapsed(true)}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i className={`fas fa-user ${collapsed ? '' : 'mr-2'}`}></i>{' '}
              {collapsed ? '' : 'Perfil'}
            </Link>
          </li>
          {config.showDonations && (
            <li>
              <Link
                to={`${process.env.PUBLIC_URL}/donations`}
                onClick={() => setCollapsed(true)}
                className={`flex ${
                  collapsed ? 'flex-row justify-center' : ''
                } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                <i
                  className={`fas fa-dollar-sign ${
                    collapsed ? '' : 'mr-2'
                  }`}></i>{' '}
                {collapsed ? '' : 'Donaciones'}
              </Link>
            </li>
          )}

          {apiHelper.isAdmin() && (
            <>
              <hr className="border-gray-600" />
              <li>
                <Link
                  to={`${process.env.PUBLIC_URL}/stats`}
                  onClick={() => setCollapsed(true)}
                  className={`flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-chart-bar ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Métricas'}
                </Link>
              </li>
              <li>
                <Link
                  to={`${process.env.PUBLIC_URL}/settings`}
                  onClick={() => setCollapsed(true)}
                  className={`flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-sliders-h ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Configuración'}
                </Link>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.updateExchangeRates();
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-dollar-sign ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar tipos de cambio'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar precios (todos)'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('tcgplayer');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar TCGPlayer'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('cardkingdom');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar CardKingdom'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('arkana');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar Arkana'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('labatikueva');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar La Batikueva'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('magiclair');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar MagicLair'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('magicz');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar MagicZ'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('mtgpirulo');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar MTG Pirulo'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('mercadiacity');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar MercadiaCity'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('magicdealers');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar MagicDealers'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('magicpalace');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar MagicPalace'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('tcgpoint');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar TCGPoint'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.crawl('cardcitadeltcg');
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i
                    className={`fas fa-database ${
                      collapsed ? '' : 'mr-2'
                    }`}></i>{' '}
                  {collapsed ? '' : 'Actualizar CardCitadelTCG'}
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    apiHelper.deleteOldPrices();
                    setCollapsed(true);
                  }}
                  className={`cursor-pointer flex ${
                    collapsed ? 'flex-row justify-center' : ''
                  } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
                  <i className={`fas fa-trash ${collapsed ? '' : 'mr-2'}`}></i>{' '}
                  {collapsed ? '' : 'Eliminar precios viejos'}
                </div>
              </li>

              <hr className="border-gray-600" />
              {IMPORT_TARGETS.map(([target, label]) => (
                <MenuAction
                  key={target}
                  collapsed={collapsed}
                  iconClass="fa-cloud-download-alt"
                  label={label}
                  // Deliberately does not collapse the menu: these take minutes, and the
                  // status line below is the only feedback there is.
                  onClick={() => onClickImport(target)}
                />
              ))}
              {!collapsed && importMessage && (
                <li className="px-2 py-1 text-xs text-gray-400">
                  {importMessage} Seguí el avance en Métricas.
                </li>
              )}
            </>
          )}
          <hr className="border-gray-600" />
          <li>
            <Link
              to="/login"
              onClick={apiHelper.logout}
              className={`flex ${
                collapsed ? 'flex-row justify-center' : ''
              } items-center p-2 text-gray-300 hover:bg-gray-700 rounded`}>
              <i
                className={`fas fa-sign-out-alt ${
                  collapsed ? '' : 'mr-2'
                }`}></i>{' '}
              {collapsed ? '' : 'Cerrar sesión'}
            </Link>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default SideMenu;
